import { useState } from 'react';
import type { LongTextConfig, LongTextValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';
import { FootnoteNote } from './FootnoteNote';

interface LongTextProps {
  field: Pick<Field, 'label' | 'allowFootnote'>;
  config: LongTextConfig;
  value: LongTextValue | null;
  onCommit: (value: LongTextValue | null) => void;
  disabled?: boolean;
}

export function LongText({ field, config, value, onCommit, disabled = false }: LongTextProps) {
  const [text, setText] = useState(value?.text ?? '');
  const [note, setNote] = useState(value?.note ?? '');

  function commit() {
    if (disabled) {
      return;
    }
    if (text.trim() === '') {
      onCommit(null);
      return;
    }
    onCommit({
      type: 'LONG_TEXT',
      text: text.slice(0, config.maxLength),
      footnote: value?.footnote ?? false,
      note: value?.note ?? null,
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-sm text-text-muted">
        <span className="eyebrow">
          {field.label} · {text.length}/{config.maxLength}
        </span>
        <textarea
          value={text}
          disabled={disabled}
          maxLength={config.maxLength}
          rows={4}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          className="rounded-md border border-border-strong bg-bg px-3 py-2 text-text focus:border-accent"
        />
      </label>
      {field.allowFootnote && (
        <FootnoteNote
          footnote={value?.footnote ?? false}
          note={note}
          disabled={disabled || !value}
          onFootnoteChange={(footnote) => value && onCommit({ ...value, footnote })}
          onNoteChange={setNote}
          onNoteBlur={() => value && onCommit({ ...value, note: note.trim() === '' ? null : note })}
        />
      )}
    </div>
  );
}
